
import { useState } from "react";
import { ChevronRight } from "lucide-react";
import { Card, CardContent } from "@shared/components/ui/card";
import { Button } from "@shared/components/ui/button";
import { sanitizeHtml } from "@shared/utils/sanitize";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/components/ui/dialog";

interface ExperienceProps {
  id: number;
  title: string;
  description: string;
  image?: string;
  price?: string;
  details?: string;
  buttonLabel?: string;
  detailLabel?: string;
  onBook?: () => void;
}

const ExperienceCard = ({ experience }: { experience: ExperienceProps }) => {
  const [detailOpen, setDetailOpen] = useState(false);

  const hasDetail = !!(experience.description || experience.details);

  return (
    <>
      <Card className="overflow-hidden hover:border-brand transition-colors group h-full flex flex-col">
        <CardContent className="p-0 flex flex-col flex-1">
          {experience.image && (
            <div className="relative">
              <div
                className="w-full h-32 bg-cover bg-center"
                style={{ backgroundImage: `url(${experience.image})` }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
              {experience.price && (
                <div className="absolute bottom-0 left-0 p-3 text-white">
                  <p className="text-xs font-medium">{experience.price}</p>
                </div>
              )}
            </div>
          )}

          <div className="p-4 flex flex-col flex-1">
            <h4 className="font-medium text-foreground mb-1" dangerouslySetInnerHTML={{ __html: sanitizeHtml(experience.title) }} />
            {experience.description && (
              <div className="mb-3">
                <span className="text-sm text-foreground/50 line-clamp-2" dangerouslySetInnerHTML={{ __html: sanitizeHtml(experience.description) }} />
                {hasDetail && (
                  <button
                    type="button"
                    onClick={() => setDetailOpen(true)}
                    className="text-sm text-brand hover:text-brand-dark font-medium ml-1"
                  >
                    {experience.detailLabel || 'View more'}
                  </button>
                )}
              </div>
            )}
            {!experience.description && hasDetail && (
              <div className="mb-3">
                <button
                  type="button"
                  onClick={() => setDetailOpen(true)}
                  className="text-sm text-brand hover:text-brand-dark font-medium"
                >
                  {experience.detailLabel || 'View more'}
                </button>
              </div>
            )}
            {!experience.image && experience.price && (
              <p className="text-sm font-medium text-brand mb-3">{experience.price}</p>
            )}

            <div className="mt-auto">
              <Button
                variant="outline"
                size="sm"
                className="w-full bg-card border-brand text-brand hover:bg-brand-muted gap-1 group-hover:bg-brand group-hover:text-brand-foreground transition-colors"
                onClick={experience.onBook}
              >
                {experience.buttonLabel || 'Book now'}
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto bg-card">
          <DialogHeader>
            <DialogTitle dangerouslySetInnerHTML={{ __html: sanitizeHtml(experience.title) }} />
          </DialogHeader>

          {experience.image && (
            <div
              className="w-full h-40 rounded-lg bg-cover bg-center"
              style={{ backgroundImage: `url(${experience.image})` }}
            />
          )}

          {experience.price && (
            <p className="text-lg font-semibold text-brand">{experience.price}</p>
          )}

          {experience.description && (
            <div
              className="text-sm text-foreground/70 prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(experience.description) }}
            />
          )}

          {experience.details && (
            <div
              className="text-sm text-foreground/70 prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(experience.details) }}
            />
          )}

          <Button
            className="w-full bg-brand text-brand-foreground hover:bg-brand-dark gap-1 mt-2"
            onClick={() => {
              setDetailOpen(false);
              experience.onBook?.();
            }}
          >
            {experience.buttonLabel || 'Book now'}
            <ChevronRight size={16} />
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ExperienceCard;
